"use client";

import { useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { IconCheck, IconX } from "@tabler/icons-react";
import { Dot, type Tone } from "./Dot";
import { StatusBadge } from "./StatusBadge";
import { SegmentedBar } from "./SegmentedBar";
export type MirrorState = "ready" | "partial" | "unknown" | "dead";

/** Result of an `onAction` invocation that the row should reflect inline.
 *  `ok` toggles the success/error styling; `message` shows up as a tooltip
 *  for hover-discovery of server-side error reasons. */
export interface MirrorActionResult {
  success: boolean;
  message: string;
}
type SendStatus = "idle" | "sending" | "success" | "error";
const DOT_TONE: Record<MirrorState, Tone> = {
  ready: "ready",
  partial: "partial",
  unknown: "unknown",
  dead: "dead"
};
const BADGE_LABEL: Record<MirrorState, string> = {
  ready: "bereit",
  partial: "teilweise",
  unknown: "ungeprüft",
  dead: "offline"
};
export interface MirrorRowProps {
  hoster: string;
  state: MirrorState;
  headline: ReactNode;
  subline?: ReactNode;
  segments: {
    tone: Tone;
    weight: number;
  }[];
  actionLabel: ReactNode;
  /** May return a Promise<MirrorActionResult> to drive the inline send-status
   *  feedback (sende… / gesendet / Fehler). Returning void keeps the legacy
   *  fire-and-forget behaviour for actions whose feedback flows externally
   *  (unlock → UnlockStatusBanner). */
  // biome-ignore lint/suspicious/noConfusingVoidType: das `void` im Union ist hier genau die Aussage — ein Handler DARF nichts zurueckgeben (Fire-and-forget, Feedback laeuft extern ueber UnlockStatusBanner) oder ein Promise mit Ergebnis (Inline-Status). `undefined` waere schwaecher: es wuerde ein explizites `return undefined` verlangen statt "Rueckgabewert wird ignoriert".
  onAction?: () => void | Promise<MirrorActionResult>;
  disabled?: boolean;
  /** External loading state (e.g. unlock-in-progress driven by parent). The
   *  internal send status takes precedence visually when active. */
  loading?: boolean;
  /** ms delay for entrance animation */
  delay?: number;
}
export function MirrorRow(props: MirrorRowProps) {
  const [sendStatus, setSendStatus] = useState<SendStatus>("idle");
  const [sendMessage, setSendMessage] = useState<string | null>(null);
  const isSending = sendStatus === "sending";
  const isLoading = props.loading || isSending;
  const isDisabled = props.disabled || isLoading;
  const isDeadOrDisabled = props.disabled || props.state === "dead";
  async function handleClick() {
    if (isDisabled) return;
    const result = props.onAction?.();
    if (!result || typeof (result as Promise<unknown>).then !== "function") return;
    setSendStatus("sending");
    setSendMessage(null);
    let final: SendStatus = "error";
    try {
      const r = await (result as Promise<MirrorActionResult>);
      final = r.success ? "success" : "error";
      setSendStatus(final);
      setSendMessage(r.message);
      // Surface the message in dev console so failures are diagnosable even
      // if the user clicked away before the inline message timed out.
      if (!r.success) console.warn(`[JD send] ${props.hoster}: ${r.message}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unbekannter Fehler";
      setSendStatus("error");
      setSendMessage(msg);
      console.error(`[JD send] ${props.hoster}:`, err);
    }
    // Errors stay visible long enough to actually read; success fades faster.
    const dwell = final === "success" ? 2400 : 6000;
    setTimeout(() => {
      setSendStatus("idle");
      setSendMessage(null);
    }, dwell);
  }
  const actionContent = (() => {
    if (sendStatus === "sending") return {
      icon: <span className="mirror-row__spinner" />,
      text: "sende…"
    };
    if (sendStatus === "success") return {
      icon: <IconCheck size={13} />,
      text: "gesendet"
    };
    if (sendStatus === "error") return {
      icon: <IconX size={13} />,
      text: "Fehler"
    };
    if (props.loading) return {
      icon: <span className="mirror-row__spinner" />,
      text: "Läuft…"
    };
    return {
      icon: null,
      text: props.actionLabel
    };
  })();
  return <motion.div className="mirror-row" data-state={props.state} data-send-status={sendStatus !== "idle" ? sendStatus : undefined} data-interactive={!isDisabled ? "true" : undefined} data-disabled={isDisabled ? "true" : undefined} layout initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: isDeadOrDisabled ? 0.6 : 1,
    y: 0
  }} transition={{
    duration: 0.26,
    ease: "easeOut",
    delay: (props.delay ?? 0) / 1000
  }} whileHover={!isDisabled ? {
    scale: 1.005
  } : undefined} whileTap={!isDisabled ? {
    scale: 0.995
  } : undefined} style={{
    cursor: isDisabled ? "not-allowed" : "pointer"
  }} onClick={handleClick} title={sendMessage ?? undefined}>
      <Dot tone={isLoading ? "unknown" : DOT_TONE[props.state]} />
      <div className="mirror-row__body">
        <div className="mirror-row__title">
          <span className="mirror-row__name">{props.hoster}</span>
          <StatusBadge tone={DOT_TONE[props.state]}>
            {props.loading ? "entsperrt…" : BADGE_LABEL[props.state]}
          </StatusBadge>
        </div>
        {/* While we have a send-result message, override the headline/subline
         *  with it so the user actually sees what JDownloader said — no need
         *  to hover for the title attribute. */}
        {sendMessage ? <div className={`mirror-row__msg ${sendStatus === "error" ? "mirror-row__msg--err" : "mirror-row__msg--ok"}`}>
            {sendMessage}
          </div> : <div className="mirror-row__subline">
            <strong>{props.headline}</strong>
            {props.subline && <>
                {" · "}
                <span>{props.subline}</span>
              </>}
          </div>}
        <SegmentedBar segments={isLoading ? [{
        tone: "unknown",
        weight: 1
      }] : props.segments} height={4} />
      </div>
      <span className="mirror-row__action">
        {actionContent.icon}
        {actionContent.text}
      </span>
    </motion.div>;
}